import React, { useState, useEffect, useMemo } from 'react';
import { fetchWithTimeout } from '../../lib/fetchWithTimeout';
import {
  FileText,
  Loader,
  AlertCircle,
  ExternalLink,
  ChevronDown,
  RefreshCcw,
} from 'lucide-react';
import { supabase, STORAGE_BUCKET } from '../../lib/supabase';

export type PublicNoticeQuestionSource =
  | 'llm'
  | 'heuristic'
  | 'heuristic_fallback';

export interface PublicNoticeQuestion {
  question_text: string;
  paragraph_number: string | null;
  paragraph_range: string;
  paragraph_text: string;
  page_number: number | null;
  short_answer: string;
  question_type: 'PUBLIC_NOTICE';
  section_heading: string | null;
}

export interface PublicNoticeQuestionsResponse {
  docket_number: string;
  public_notice_filename: string;
  cache_version: number;
  total_questions: number;
  source: PublicNoticeQuestionSource;
  all_questions: PublicNoticeQuestion[];
}

interface PublicNoticeDocumentRef {
  filename?: string;
  src?: string;
  url?: string;
  file_url?: string;
  document_url?: string;
  link?: string;
  href?: string;
  mime_type?: string;
  type?: string;
  [k: string]: unknown;
}

interface PublicNoticeFiling {
  id_submission: string;
  date_received: string | null;
  filer_name: string | null;
  submissiontype_short: string | null;
  documents: PublicNoticeDocumentRef[];
}

interface PublicNoticePreviewSectionProps {
  docketNumber: string;
  publicNoticeCount: number | null;
  proceedingName?: string;
  publicNoticeDocument: PublicNoticeFiling | null;
}

const RAG_API_HOST = 'https://fcc-summarize-app-r4ac3.ondigitalocean.app';

function getDocUrl(doc: PublicNoticeDocumentRef | null | undefined): string | null {
  if (!doc) return null;
  return (
    (doc.src as string | undefined) ||
    (doc.url as string | undefined) ||
    (doc.file_url as string | undefined) ||
    (doc.document_url as string | undefined) ||
    (doc.link as string | undefined) ||
    (doc.href as string | undefined) ||
    null
  );
}

/**
 * Pick the primary PDF document from a public notice filing. Prefers .pdf
 * filename or pdf mime type; falls back to the first document. Mirrors the
 * selection used by `ProceedingDetailsPage.getPublicNoticeDocumentUrl`.
 */
function pickPrimaryPnDocument(
  documents: PublicNoticeDocumentRef[] | undefined
): PublicNoticeDocumentRef | null {
  if (!documents || documents.length === 0) return null;
  const pdfDoc = documents.find((doc) => {
    const u = getDocUrl(doc);
    const filename = (doc?.filename || '').toString().toLowerCase();
    const mime = (doc?.mime_type || doc?.type || '').toString().toLowerCase();
    if (filename.endsWith('.pdf')) return true;
    if (mime.includes('pdf')) return true;
    if (u && String(u).toLowerCase().endsWith('.pdf')) return true;
    return false;
  });
  return pdfDoc || documents[0] || null;
}

export const PublicNoticePreviewSection: React.FC<PublicNoticePreviewSectionProps> = ({
  docketNumber,
  publicNoticeCount,
  publicNoticeDocument,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pnFileInStorage, setPnFileInStorage] = useState<string | null>(null);
  const [uploadingPn, setUploadingPn] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [questions, setQuestions] = useState<PublicNoticeQuestion[]>([]);
  const [questionsMeta, setQuestionsMeta] = useState<{
    source: PublicNoticeQuestionSource | null;
    total: number;
  }>({ source: null, total: 0 });
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [reprocessError, setReprocessError] = useState<string | null>(null);
  const [reprocessSuccessAt, setReprocessSuccessAt] = useState<string | null>(null);

  const primaryDoc = useMemo(
    () => pickPrimaryPnDocument(publicNoticeDocument?.documents),
    [publicNoticeDocument]
  );
  const primaryFilename = primaryDoc?.filename || null;

  useEffect(() => {
    let isMounted = true;
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!isMounted) return;
        setIsAuthenticated(!!user);
      } catch {
        if (!isMounted) return;
        setIsAuthenticated(false);
      }
    };

    checkAuth();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event: string, session: { user?: unknown } | null) => {
        if (!isMounted) return;
        setIsAuthenticated(!!session?.user);
      }
    );

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Check storage / upload the PN PDF to ecfs/{docket}/PUBLIC-NOTICE/ so the
  // backend extractor can find it.
  useEffect(() => {
    if (!docketNumber) {
      setLoading(false);
      return;
    }
    if (publicNoticeCount === null || publicNoticeCount === 0) {
      setLoading(false);
      return;
    }
    if (!publicNoticeDocument || !primaryDoc || !primaryFilename) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const checkAndUploadPn = async () => {
      try {
        setLoading(true);
        setError(null);

        const srcUrl = getDocUrl(primaryDoc);
        if (srcUrl) {
          setDocumentUrl(srcUrl);
        }

        const filerName = publicNoticeDocument.filer_name || 'FCC';
        const submissionId = publicNoticeDocument.id_submission;
        const sanitizedFilerName = filerName
          .replace(/[<>:"/\\|?*]/g, '')
          .replace(/\s+/g, '_');

        const possiblePaths = [
          `ecfs/${docketNumber}/PUBLIC-NOTICE/${submissionId}___${sanitizedFilerName}___${primaryFilename}`,
          `ecfs/${docketNumber}/PUBLIC-NOTICE/${primaryFilename}`,
        ];

        let fileFound = false;
        for (const path of possiblePaths) {
          try {
            const { data, error: listErr } = await supabase.storage
              .from(STORAGE_BUCKET)
              .list(path.split('/').slice(0, -1).join('/'), { limit: 1000 });
            if (!listErr && data) {
              const fileName = path.split('/').pop();
              const found = (data as Array<{ name: string }>).find(
                (file) => file.name === fileName
              );
              if (found) {
                if (cancelled) return;
                setPnFileInStorage(path);
                fileFound = true;
                break;
              }
            }
          } catch {
            // try next candidate path
          }
        }

        if (!fileFound && srcUrl) {
          if (cancelled) return;
          setUploadingPn(true);
          try {
            const uploadApiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-ecfs-files`;
            const uploadResponse = await fetch(uploadApiUrl, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                src: srcUrl,
                docketNumber,
                filerName,
                fileName: primaryFilename,
                submissionId,
                // Hyphenated form is preserved by upload-ecfs-files sanitizer
                // and matches the backend's expected ecfs/{docket}/PUBLIC-NOTICE/ path.
                filingType: 'PUBLIC-NOTICE',
                expressComment: null,
              }),
            });

            if (uploadResponse.ok) {
              const uploadResult = await uploadResponse.json();
              if (uploadResult.path && !cancelled) {
                setPnFileInStorage(uploadResult.path);
              }
            } else {
              console.warn(
                'Failed to upload Public Notice file to storage:',
                await uploadResponse.text().catch(() => '')
              );
            }
          } catch (uploadErr) {
            console.error('Error uploading Public Notice file:', uploadErr);
          } finally {
            if (!cancelled) setUploadingPn(false);
          }
        }
      } catch (err) {
        console.error('Error checking/uploading Public Notice file:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to prepare Public Notice file');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkAndUploadPn();
    return () => {
      cancelled = true;
    };
  }, [docketNumber, publicNoticeCount, publicNoticeDocument, primaryDoc, primaryFilename]);

  // Cache helpers under summaries/{docket}/PUBLIC-NOTICE/public_notice_questions.json
  const loadCachedQuestions = async (
    docketNum: string
  ): Promise<PublicNoticeQuestionsResponse | null> => {
    try {
      const path = `summaries/${docketNum}/PUBLIC-NOTICE/public_notice_questions.json`;
      const { data, error: dlErr } = await supabase.storage
        .from(STORAGE_BUCKET)
        .download(path);
      if (dlErr || !data) return null;
      const text = await data.text();
      return JSON.parse(text) as PublicNoticeQuestionsResponse;
    } catch {
      return null;
    }
  };

  const saveQuestionsToCache = async (
    docketNum: string,
    response: PublicNoticeQuestionsResponse
  ) => {
    try {
      const path = `summaries/${docketNum}/PUBLIC-NOTICE/public_notice_questions.json`;
      const payload = { ...response, cached_at: new Date().toISOString() };
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      });
      const { error: upErr } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(path, blob, { contentType: 'application/json', upsert: true });
      if (upErr) {
        console.warn('Failed to cache Public Notice questions:', upErr);
      }
    } catch (err) {
      console.warn('Error caching Public Notice questions:', err);
    }
  };

  const applyQuestionsResult = (result: PublicNoticeQuestionsResponse) => {
    setQuestions(Array.isArray(result.all_questions) ? result.all_questions : []);
    setQuestionsMeta({
      source: result.source ?? null,
      total: typeof result.total_questions === 'number'
        ? result.total_questions
        : (result.all_questions?.length ?? 0),
    });
  };

  // Fetch PN questions once the file is known to be in storage.
  useEffect(() => {
    if (!pnFileInStorage || !docketNumber) return;

    let cancelled = false;

    const fetchQuestions = async () => {
      try {
        setLoadingQuestions(true);
        setQuestionsError(null);
        setReprocessError(null);

        const cached = await loadCachedQuestions(docketNumber);
        if (cached && Array.isArray(cached.all_questions)) {
          if (cancelled) return;
          applyQuestionsResult(cached);
          setLoadingQuestions(false);
          return;
        }

        const storageFilename =
          pnFileInStorage.split('/').pop() || primaryFilename || '';
        if (!storageFilename) {
          throw new Error('Missing Public Notice filename');
        }

        const params = new URLSearchParams({
          docket_number: docketNumber,
          public_notice_filename: storageFilename,
          method: 'llm',
        });
        const apiUrl = `${RAG_API_HOST}/public_notice_questions?${params.toString()}`;

        const response = await fetchWithTimeout(apiUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          throw new Error(
            `Failed to fetch Public Notice questions: ${response.status} - ${errorText}`
          );
        }

        const result = await response.json();
        if (cancelled) return;

        // Long Public Notices return {status:'processing'} immediately and
        // write the result to storage when done. Without handling this, an
        // in-flight response carries no all_questions, so we'd render the
        // definitive "This Public Notice does not appear to solicit comment"
        // state while the backend is still working — confidently wrong on a
        // legal-research surface. Keep the loading spinner up and poll the
        // cache until the file lands, then apply whatever it holds. Break on
        // file *presence*, not on question count: an empty result IS the real
        // "no questions" answer, so a genuinely comment-free PN resolves
        // instead of spinning for the full timeout.
        if ((result as { status?: string })?.status === 'processing') {
          const POLL_INTERVAL_MS = 4000;
          const MAX_ATTEMPTS = 75; // ~5 minutes
          let landed: PublicNoticeQuestionsResponse | null = null;
          for (let attempt = 1; attempt <= MAX_ATTEMPTS && !cancelled; attempt += 1) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
            landed = await loadCachedQuestions(docketNumber);
            if (landed) break;
          }
          if (cancelled) return;
          if (!landed) {
            throw new Error(
              `Public Notice analysis did not complete within ${(MAX_ATTEMPTS * POLL_INTERVAL_MS) / 1000}s. Try again later.`
            );
          }
          applyQuestionsResult(landed);
        } else {
          // Synchronous payload (cache hit, or a backend that answers inline).
          const inline = result as PublicNoticeQuestionsResponse;
          applyQuestionsResult(inline);
          if (Array.isArray(inline.all_questions) && inline.all_questions.length > 0) {
            await saveQuestionsToCache(docketNumber, inline);
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching Public Notice questions:', err);
        setQuestionsError(
          err instanceof Error ? err.message : 'Failed to fetch Public Notice questions'
        );
      } finally {
        if (!cancelled) setLoadingQuestions(false);
      }
    };

    fetchQuestions();

    return () => {
      cancelled = true;
    };
  }, [docketNumber, pnFileInStorage, primaryFilename]);

  const handleReprocessQuestions = async () => {
    if (!isAuthenticated) return;
    if (!docketNumber || !pnFileInStorage) return;
    if (isReprocessing || loadingQuestions || uploadingPn) return;

    const confirmed = window.confirm(
      'Reprocess Public Notice questions now? This will regenerate questions and overwrite the cached results for this docket.'
    );
    if (!confirmed) return;

    try {
      setIsReprocessing(true);
      setReprocessError(null);
      setReprocessSuccessAt(null);
      setQuestionsError(null);

      const storageFilename =
        pnFileInStorage.split('/').pop() || primaryFilename || '';
      const params = new URLSearchParams({
        docket_number: docketNumber,
        public_notice_filename: storageFilename,
        method: 'llm',
        use_cache: 'false',
      });
      const apiUrl = `${RAG_API_HOST}/public_notice_questions?${params.toString()}`;

      const response = await fetchWithTimeout(apiUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Failed to reprocess questions: ${response.status} - ${errorText}`);
      }

      const result: PublicNoticeQuestionsResponse = await response.json();
      applyQuestionsResult(result);
      await saveQuestionsToCache(docketNumber, result);

      setReprocessSuccessAt(new Date().toISOString());
    } catch (err) {
      console.error('Error reprocessing Public Notice questions:', err);
      setReprocessError(
        err instanceof Error ? err.message : 'Failed to reprocess Public Notice questions'
      );
    } finally {
      setIsReprocessing(false);
    }
  };

  if (publicNoticeCount === null || publicNoticeCount === 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="mb-8 backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-center py-8">
          <Loader className="w-6 h-6 text-green-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading Public Notice questions...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8 backdrop-blur-xl bg-white/90 border border-red-300/50 rounded-lg p-6 shadow-sm">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-800 font-semibold mb-2">Error Loading Public Notice</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!publicNoticeDocument || !primaryDoc || !primaryFilename) {
    return (
      <div className="mb-8 backdrop-blur-xl bg-white/90 border border-yellow-300/60 rounded-lg p-6 shadow-sm">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-yellow-800 font-semibold mb-1">Public Notice not available</h3>
            <p className="text-yellow-700 text-sm">
              No Public Notice document could be located for this docket yet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-lg p-6 shadow-sm">
      <div className="flex items-center space-x-3 mb-6">
        <FileText className="w-6 h-6 text-green-600" />
        <h2 className="text-2xl font-bold text-black">Public Notice Questions</h2>
      </div>

      {documentUrl && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 mb-1 truncate">
                {primaryFilename}
              </h3>
              {publicNoticeDocument.date_received && (
                <p className="text-sm text-gray-600">
                  Released:{' '}
                  {new Date(publicNoticeDocument.date_received).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: 'UTC',
                  })}
                </p>
              )}
              {publicNoticeDocument.filer_name && (
                <p className="text-xs text-gray-500 mt-1">
                  Filer: {publicNoticeDocument.filer_name}
                </p>
              )}
            </div>
            <a
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors whitespace-nowrap"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View Full Document</span>
            </a>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 gap-4">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Public Notice Questions
              {questions.length > 0 && ` (${questions.length})`}
            </h3>
          </div>

          {isAuthenticated && (
            <button
              type="button"
              onClick={handleReprocessQuestions}
              disabled={!pnFileInStorage || loadingQuestions || uploadingPn || isReprocessing}
              className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reprocess Public Notice questions"
            >
              {isReprocessing ? (
                <Loader className="w-4 h-4 animate-spin text-gray-500" />
              ) : (
                <RefreshCcw className="w-4 h-4 text-gray-500" />
              )}
              <span>{isReprocessing ? 'Reprocessing…' : 'Reprocess'}</span>
            </button>
          )}
        </div>

        {isAuthenticated && (reprocessError || reprocessSuccessAt) && (
          <div className="mb-3">
            {reprocessError ? (
              <p className="text-sm text-red-700">{reprocessError}</p>
            ) : (
              <p className="text-sm text-green-700">
                Reprocessed{' '}
                {reprocessSuccessAt ? `at ${new Date(reprocessSuccessAt).toLocaleString()}` : ''}.
              </p>
            )}
          </div>
        )}

        {uploadingPn ? (
          <div className="flex items-center space-x-2 py-4">
            <Loader className="w-4 h-4 animate-spin text-gray-400" />
            <span className="text-gray-600">Uploading Public Notice file to storage...</span>
          </div>
        ) : !pnFileInStorage ? (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
            Public Notice file needs to be uploaded to storage before questions can be extracted. This should happen automatically...
          </div>
        ) : loadingQuestions ? (
          <div className="flex items-center space-x-2 py-4">
            <Loader className="w-4 h-4 animate-spin text-gray-400" />
            <span className="text-gray-600">Loading Public Notice questions...</span>
          </div>
        ) : questionsError ? (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
            {questionsError}
          </div>
        ) : questions.length > 0 ? (
          <div>
            <div className="max-h-[min(70vh,720px)] space-y-2.5 overflow-y-auto pr-1">
              {questions.map((question, idx) => {
                const isExpanded = expandedQuestions.has(idx);
                const qNumber = question.paragraph_number || String(idx + 1);
                return (
                  <div
                    key={`pn-q-${idx}`}
                    className="overflow-hidden rounded-lg border border-gray-200 bg-white transition-colors hover:border-gray-300"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const newExpanded = new Set(expandedQuestions);
                        if (newExpanded.has(idx)) {
                          newExpanded.delete(idx);
                        } else {
                          newExpanded.add(idx);
                        }
                        setExpandedQuestions(newExpanded);
                      }}
                      className="w-full px-4 py-3.5 text-left transition-colors hover:bg-gray-50/80"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="mb-1.5 flex flex-wrap items-center gap-2">
                            <span className="inline-flex shrink-0 items-center rounded bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
                              Q{qNumber}
                            </span>
                            {question.section_heading && (
                              <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                                {question.section_heading}
                              </span>
                            )}
                            {typeof question.page_number === 'number' && (
                              <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                p. {question.page_number}
                              </span>
                            )}
                          </div>
                          <p
                            className={`text-sm font-normal leading-relaxed ${
                              isExpanded ? 'text-gray-900' : 'line-clamp-2 text-gray-600'
                            }`}
                          >
                            {question.question_text}
                          </p>
                          {question.short_answer && !isExpanded && (
                            <p className="mt-1 text-xs text-gray-500 line-clamp-1">
                              {question.short_answer}
                            </p>
                          )}
                        </div>
                        <ChevronDown
                          className={`mt-0.5 h-6 w-6 shrink-0 text-gray-500 transition-transform ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                          aria-hidden
                        />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50 space-y-3">
                        {question.short_answer && (
                          <div>
                            <h5 className="text-xs font-semibold text-gray-700 mb-1">
                              What the Commission wants to know:
                            </h5>
                            <p className="text-xs text-gray-600 leading-relaxed">
                              {question.short_answer}
                            </p>
                          </div>
                        )}
                        {question.paragraph_text && (
                          <div>
                            <h5 className="text-xs font-semibold text-gray-700 mb-1">Context:</h5>
                            <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">
                              {question.paragraph_text}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {questionsMeta.source && (
              <p className="mt-3 text-xs text-gray-500">
                Source: {questionsMeta.source.replace(/_/g, ' ')}
              </p>
            )}
          </div>
        ) : (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
            <p className="mb-1">This Public Notice does not appear to solicit comment.</p>
            <p className="text-xs text-gray-500">
              No commenter-directed questions were extracted from this document.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
